import { jsPDF } from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const productImgUrl = (p) => {
  const raw = p.image_path || p.image_url || "";
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
  return `${API}/files/${raw}`;
};

const loadImg = async (url) => {
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    const obj = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = obj; });
    return img;
  } catch { return null; }
};

const wrapLines = (ctx, text, maxW) => {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
  });
  if (cur) lines.push(cur);
  return lines.slice(0, 2);
};

// A4 @150dpi, 2x2 grid — big photo + name only (no prices).
const W = 1240, H = 1754, M = 50, GAP = 30;
const drawPage = (ctx, items) => {
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
  const cw = (W - M * 2 - GAP) / 2, ch = (H - M * 2 - GAP) / 2;
  items.forEach(({ p, img }, i) => {
    const x = W - M - cw - (i % 2) * (cw + GAP);
    const y = M + Math.floor(i / 2) * (ch + GAP);
    ctx.strokeStyle = "#E2E8F0"; ctx.lineWidth = 3;
    ctx.strokeRect(x, y, cw, ch);
    const nameH = 170, boxW = cw - 40, boxH = ch - nameH - 40;
    if (img) {
      const s = Math.min(boxW / img.width, boxH / img.height);
      const iw = img.width * s, ih = img.height * s;
      ctx.drawImage(img, x + 20 + (boxW - iw) / 2, y + 20 + (boxH - ih) / 2, iw, ih);
    } else {
      ctx.fillStyle = "#F1F5F9"; ctx.fillRect(x + 20, y + 20, boxW, boxH);
    }
    ctx.fillStyle = "#0F172A"; ctx.font = "800 46px Cairo, Tajawal, Tahoma, sans-serif";
    ctx.direction = "rtl"; ctx.textAlign = "center";
    wrapLines(ctx, p.name_ar || p.name, cw - 40).forEach((ln, li) =>
      ctx.fillText(ln, x + cw / 2, y + ch - nameH + 60 + li * 62));
  });
};

export const buildPhotosPdf = async (products) => {
  try { await document.fonts.load("800 46px Cairo"); } catch { /* fallback font */ }
  const imgs = await Promise.all(products.map((p) => loadImg(productImgUrl(p))));
  const pdf = new jsPDF({ unit: "px", format: [W, H], orientation: "portrait" });
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  for (let i = 0; i < products.length; i += 4) {
    if (i > 0) pdf.addPage([W, H], "portrait");
    drawPage(ctx, products.slice(i, i + 4).map((p, k) => ({ p, img: imgs[i + k] })));
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", 0, 0, W, H);
  }
  return pdf.output("blob");
};

const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export const buildPhotosPrintHtml = (products) => {
  const pages = [];
  for (let i = 0; i < products.length; i += 4) pages.push(products.slice(i, i + 4));
  return `<!doctype html><html dir="rtl"><head><meta charset="utf-8"><link href="https://fonts.googleapis.com/css2?family=Cairo:wght@800&display=swap" rel="stylesheet"><style>
    @page { size: A4; margin: 8mm; }
    body { margin: 0; font-family: Cairo, Tajawal, Tahoma, sans-serif; }
    .page { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 6mm; height: 279mm; page-break-after: always; }
    .page:last-child { page-break-after: auto; }
    .cell { border: 1px solid #E2E8F0; border-radius: 4mm; display: flex; flex-direction: column; padding: 4mm; overflow: hidden; }
    .img { flex: 1; display: flex; align-items: center; justify-content: center; min-height: 0; }
    .img img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .name { font-size: 18pt; font-weight: 900; text-align: center; padding-top: 3mm; color: #0F172A; }
  </style></head><body>${pages.map((pg) => `<div class="page">${pg.map((p) => `
    <div class="cell"><div class="img">${productImgUrl(p) ? `<img src="${esc(productImgUrl(p))}"/>` : ""}</div>
    <div class="name">${esc(p.name_ar || p.name)}</div></div>`).join("")}</div>`).join("")}</body></html>`;
};
